import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
export const FILE_BASE = `${BACKEND_URL}`;

export const api = axios.create({ baseURL: API });

/**
 * Resolve the current tenant slug. Order of precedence — URL FIRST,
 * because in the Capacitor native shell the WebView cold-loads the
 * tenant-specific `server.url` baked into `capacitor.config.json` at
 * build time (which is always `https://<host>/t/<slug>/` for non-default
 * tenants — see `fcm.py::build_config_for`). Trusting the URL over
 * localStorage guarantees each installed tenant app can NEVER be
 * poisoned by a stale `kb_tenant` value left behind by a dev override,
 * a push deep-link, or a previous session — which was the root cause
 * of tenant-A apps occasionally rendering tenant-B's catalog.
 *
 *   1. URL path prefix `/t/{slug}/...`             — authoritative
 *   2. Real subdomain on a production platform host — authoritative
 *   3. localStorage `kb_tenant`                     — dev/admin override only
 *   4. fallback "default"                            — public web root only
 */
// Hosts that identify the platform itself (not a tenant subdomain).
// Any subdomain matching one of these prefixes, or any host ending in
// one of these suffixes, is treated as the platform root — not a tenant.
const PLATFORM_SUB_PREFIXES = new Set([
  "www", "preview", "api", "admin", "backend",
  "ecommerce", "ecommerceapi",
  "fashion-collective-18", "vendor-hub-457",
]);
const PLATFORM_HOST_SUFFIXES = [".preview.emergentagent.com"];

export function resolveTenantSlug() {
  if (typeof window !== "undefined") {
    // 1. URL path prefix — the ONLY signal a native Capacitor build
    //    can rely on, because build_app.py bakes `/t/<slug>/` into
    //    `server.url`. Any other signal (localStorage, subdomain) is
    //    mutable at runtime and can leak state across tenants on the
    //    same device or across sessions.
    const parts = window.location.pathname.split("/").filter(Boolean);
    if (parts[0] === "t" && parts[1]) return parts[1];

    // 2. Custom-domain / subdomain — only meaningful when the host has
    //    a real tenant subdomain in front of the platform base domain.
    //    Explicitly skip Emergent's preview host and the platform's
    //    own subdomains (ecommerce, ecommerceapi, admin, …) — those
    //    are NOT tenants.
    const host = (window.location.hostname || "").toLowerCase();
    const isPlatformHost = PLATFORM_HOST_SUFFIXES.some((s) => host.endsWith(s));
    if (!isPlatformHost && host.split(".").length >= 3) {
      const sub = host.split(".")[0];
      if (sub && !PLATFORM_SUB_PREFIXES.has(sub)) return sub;
    }

    // 3. localStorage override — LAST resort. Only used when URL and
    //    host haven't dictated a tenant (e.g. on the public web root
    //    of the platform where an admin has manually switched tenant
    //    via /superadmin/tenants). Never wins over a URL/host signal.
    try {
      const ls = localStorage.getItem("kb_tenant");
      if (ls && ls !== "default") return ls;
    } catch { /* SSR / private mode */ }
  }
  return "default";
}

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("kb_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  const slug = resolveTenantSlug();
  if (slug) config.headers["X-Tenant-Slug"] = slug;
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      localStorage.removeItem("kb_token");
    }
    return Promise.reject(err);
  }
);

export const imgUrl = (pathOrUrl) => {
  if (!pathOrUrl || typeof pathOrUrl !== "string") return "";
  const s = pathOrUrl.trim();
  if (!s) return "";
  if (s.startsWith("http://") || s.startsWith("https://") || s.startsWith("data:")) return s;
  if (s.startsWith("/api/")) return `${BACKEND_URL}${s}`;
  return `${API}/uploads/file/${s}`;
};

export const money = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);
